import { QuestionIcon } from '@chakra-ui/icons'
import {
  Box,
  Center,
  Divider,
  FormControl,
  Heading,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  Spacer,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Tr,
} from '@chakra-ui/react'
import { Range, UserLab, UserPanel, UserPanelItem } from '../../types/bloodWork'
import { Timestamp } from 'firebase/firestore'
import BasicDatePicker from '../../components/BasicDatePicker'
import { Fragment, useState, useRef } from 'react'
import BloodWorkActionButtons from './BloodWorkActionButtons'
import ProvocationTypeSelector from './ProvocationTypeSelector'
import { calculatedFields } from './LabTestCalculatedFields'
import useConfigList from '../../hooks/useConfigList'
import {
  isPanelRequiringProvocation,
  isPanelFilledOut,
  isProvocationTypeValid,
} from '../../utils/labHelpers'
import { useToast } from '@chakra-ui/react'

type CalculatedValues = {
  dividend?: string | number
  divisor?: string | number
}

export default ({
  userLab,
  setUserLab,
  onSubmitFunc,
  submitButtonText,
  submitButtonColorScheme,
  cancelFunc,
  archiveFunc,
  readOnly,
}: {
  userLab: UserLab
  setUserLab: React.Dispatch<React.SetStateAction<UserLab | undefined>>
  onSubmitFunc?: (data: UserLab) => Promise<void> | undefined
  submitButtonText?: string
  submitButtonColorScheme?: string
  cancelFunc?: () => void | undefined
  archiveFunc?: () => Promise<void> | undefined
  readOnly?: boolean
}) => {
  const bloodWorkUnits = useConfigList('measurement-units')
  const toast = useToast()
  const [buttonLoading, setButtonLoading] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [formReadOnly, setFormReadOnly] = useState(readOnly)
  const [validationErrors, setValidationErrors] = useState<
    Record<number, boolean>
  >({})
  const panelRefs = useRef<Record<number, HTMLDivElement | null>>({})

  const [year, setYear] = useState(
    userLab.date.toDate().getFullYear().toString()
  )
  const monthNum = userLab.date.toDate().getMonth() + 1
  const [month, setMonth] = useState(
    monthNum > 9 ? monthNum.toString() : '0' + monthNum
  )
  const dayNum = userLab.date.toDate().getDate()
  const [day, setDay] = useState(dayNum > 9 ? dayNum.toString() : '0' + dayNum)

  const formatRange = (range: Range, unit: string) => {
    return `${range.bottom} - ${range.top} ${bloodWorkUnits[unit]}`
  }

  const wrappedSubmitFunc = onSubmitFunc
    ? async () => {
        const data = { ...userLab }
        if (data) {
          // Validate provocation type for panels that require it
          const errors: Record<number, boolean> = {}
          let hasError = false

          data.panels.forEach((panel, index) => {
            if (isPanelRequiringProvocation(panel) && isPanelFilledOut(panel)) {
              if (!isProvocationTypeValid(panel.provocationType)) {
                errors[index] = true
                hasError = true

                // Show toast for first error only
                if (!hasError || Object.keys(errors).length === 1) {
                  toast({
                    status: 'warning',
                    title: 'Provocation Type Required',
                    description: `Please select at least one provocation type for the "${panel.title}" panel before saving.`,
                    duration: 5000,
                    isClosable: true,
                  })
                }
              }
            }
          })

          setValidationErrors(errors)

          if (hasError) {
            // Scroll to first invalid panel
            const firstErrorIndex = Object.keys(errors)
              .map(Number)
              .sort((a, b) => a - b)[0]
            const panelElement = panelRefs.current[firstErrorIndex]
            if (panelElement) {
              panelElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              })
            }
            return
          }

          data.date = Timestamp.fromDate(
            new Date(`${year}-${month}-${day}T00:00:00`)
          )
          setButtonDisabled(true)
          setButtonLoading(true)
          setFormReadOnly(true)
          onSubmitFunc(data)?.then(() => {
            setButtonDisabled(false)
            setButtonLoading(false)
            setFormReadOnly(false)
          })
        }
      }
    : undefined

  const wrappedArchiveFunc = archiveFunc
    ? async () => {
        setButtonDisabled(true)
        setButtonLoading(true)
        archiveFunc()?.then(() => {
          setButtonDisabled(false)
          setButtonLoading(false)
        })
      }
    : undefined

  const updateLabItem = (
    panelIndex: number,
    itemIndex: number,
    itemData: Partial<UserPanelItem>
  ) => {
    setUserLab({
      ...userLab,
      panels: userLab.panels.map((panel, panelIdx) => {
        if (panelIdx !== panelIndex) return panel
        return {
          ...panel,
          items: panel.items.map((item, itemIdx) => {
            //itemData contains either the dividend or divisor data {result: #}
            const targetItem = panel.items[itemIndex]
            const calculatedField = calculatedFields.find(
              (field) => field.title === item.title
            )
            if (calculatedField) {
              //is item being updated apart of this calculation? if not, skip.
              if (
                [calculatedField.dividend, calculatedField.divisor].includes(
                  targetItem.title
                )
              ) {
                const values: CalculatedValues = {}

                if (calculatedField.dividend === targetItem.title) {
                  values.dividend = itemData.result
                }
                if (calculatedField.divisor === targetItem.title) {
                  values.divisor = itemData.result
                }
                const calculatedResult = calculateRatio(
                  panel,
                  calculatedField.dividend,
                  calculatedField.divisor,
                  values
                )
                return { ...item, result: calculatedResult }
              }
            }
            if (itemIdx !== itemIndex) return item
            return {
              ...item,
              ...itemData,
            }
          }),
        }
      }), //end panels
    }) //end setUserLab
  }

  const updatePanelMetadata = (
    panelIndex: number,
    metadata: Partial<UserPanel>
  ) => {
    // Clear validation error when provocation type is updated
    if (
      metadata.provocationType !== undefined &&
      validationErrors[panelIndex]
    ) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[panelIndex]
        return newErrors
      })
    }

    setUserLab({
      ...userLab,
      panels: userLab.panels.map((panel, panelIdx) => {
        if (panelIdx !== panelIndex) return panel
        return {
          ...panel,
          ...metadata,
        }
      }),
    })
  }

  return (
    <Stack spacing={5}>
      {userLab && (
        <FormControl>
          <BloodWorkActionButtons
            onSubmitFunc={wrappedSubmitFunc}
            submitButtonText={submitButtonText}
            submitButtonColorScheme={submitButtonColorScheme}
            isLoading={buttonLoading}
            isDisabled={buttonDisabled}
            cancelFunc={cancelFunc}
            archiveFunc={wrappedArchiveFunc}
          />
          <Stack mt='5'>
            <TableContainer>
              <Table size='sm' variant='unstyled'>
                <Tbody>
                  <Tr>
                    <Th>
                      <Heading size='md'>Lab Date</Heading>
                      <Divider mb='5' />
                      <BasicDatePicker
                        startYear={'1920'}
                        endYear={new Date().getFullYear().toString()}
                        year={year}
                        setYear={setYear}
                        month={month}
                        setMonth={setMonth}
                        day={day}
                        setDay={setDay}
                      />
                    </Th>
                  </Tr>
                  {userLab.panels?.map((panel, panelIndex) => {
                    return (
                      <Fragment key={`panel-${panelIndex}`}>
                        <Tr>
                          <Th colSpan={5}>
                            <Box
                              ref={(el) => (panelRefs.current[panelIndex] = el)}
                            >
                              <Heading mt='5' size='md'>
                                {panel.title}
                              </Heading>
                              {isPanelRequiringProvocation(panel) && (
                                <ProvocationTypeSelector
                                  value={panel.provocationType}
                                  onChange={(value) => {
                                    updatePanelMetadata(panelIndex, {
                                      provocationType: value,
                                    })
                                  }}
                                  isDisabled={formReadOnly}
                                  isInvalid={
                                    validationErrors[panelIndex] || false
                                  }
                                />
                              )}
                            </Box>
                            <Divider />
                          </Th>
                        </Tr>
                        <Tr>
                          <Th>Test</Th>
                          <Th>Result</Th>
                          <Th>Reference Range</Th>
                          <Th>
                            Optimal Range!{' '}
                            <Popover>
                              <PopoverTrigger>
                                <QuestionIcon color='gray.400' />
                              </PopoverTrigger>
                              <PopoverContent>
                                <PopoverArrow />
                                <PopoverCloseButton />
                                <PopoverHeader
                                  fontWeight='normal'
                                  textTransform='none'
                                  whiteSpace='normal'
                                  fontSize='md'
                                >
                                  Optimal Range
                                </PopoverHeader>
                                <PopoverBody
                                  fontWeight='normal'
                                  textTransform='none'
                                  whiteSpace='normal'
                                  fontSize='md'
                                >
                                  Optimal range will only appear if it is
                                  different from the reference range.
                                </PopoverBody>
                              </PopoverContent>
                            </Popover>
                          </Th>
                        </Tr>
                        {panel.items?.map(
                          ({ title, unit, units, result }, itemIndex) => {
                            //Calculated fields display as text instead of input
                            let fieldType = ''
                            calculatedFields.forEach((field) => {
                              if (field.title === title) {
                                fieldType = 'hidden'
                              }
                            })

                            let inputField = (
                              <Input
                                id={`${panelIndex}-${itemIndex}`}
                                size='sm'
                                type={fieldType}
                                readOnly={formReadOnly}
                                placeholder='Result'
                                maxW='80px'
                                value={result}
                                onChange={(e) => {
                                  updateLabItem(panelIndex, itemIndex, {
                                    result: e.target.value,
                                  })
                                }}
                              />
                            )

                            let referenceRange = units?.find((possibleUnit) => {
                              return possibleUnit.id === unit
                            })?.referenceRange
                            if (!referenceRange) {
                              referenceRange = { top: 0, bottom: 0 }
                            }

                            let optimalRange = units?.find((possibleUnit) => {
                              return possibleUnit.id === unit
                            })?.optimalRange

                            const multipleUnits = (units?.length ?? 0) > 1

                            if (
                              referenceRange.bottom
                                .toString()
                                .toLocaleLowerCase() === 'neg'
                            ) {
                              const negPosValues = ['Neg', 'Pos']
                              inputField = (
                                <Select
                                  value={result}
                                  onChange={(e) => {
                                    if (
                                      !negPosValues.includes(e.target.value)
                                    ) {
                                      return
                                    }
                                    updateLabItem(panelIndex, itemIndex, {
                                      result: e.target.value,
                                    })
                                  }}
                                >
                                  <option value=''>Select...</option>
                                  {negPosValues.map((value, valueIdx) => {
                                    return (
                                      <option
                                        key={`${panelIndex}-${itemIndex}-${valueIdx}`}
                                        value={value}
                                      >
                                        {value}
                                      </option>
                                    )
                                  })}
                                </Select>
                              )
                            }

                            return (
                              <Tr key={`${panelIndex}-${itemIndex}-row`}>
                                <Td>
                                  <HStack direction={'row'}>
                                    <Box>{title}</Box>
                                    {multipleUnits && (
                                      <>
                                        <Spacer />
                                        <Box>Units:</Box>
                                        <Select
                                          width={'auto'}
                                          value={unit}
                                          onChange={(e) => {
                                            updateLabItem(
                                              panelIndex,
                                              itemIndex,
                                              {
                                                unit: e.target.value,
                                              }
                                            )
                                          }}
                                        >
                                          {units?.map((unitOption, unitIdx) => {
                                            return (
                                              <option
                                                key={`${panelIndex}-${itemIndex}-${unitIdx}-unit`}
                                                value={unitOption.id}
                                              >
                                                {bloodWorkUnits[unitOption.id]}
                                              </option>
                                            )
                                          })}
                                        </Select>
                                      </>
                                    )}
                                  </HStack>
                                </Td>
                                <Td>
                                  {fieldType === 'hidden' && (
                                    <Center>{result} %</Center>
                                  )}
                                  {fieldType !== 'hidden' && inputField}
                                </Td>
                                <Td>{formatRange(referenceRange, unit)}</Td>
                                <Td>
                                  {optimalRange
                                    ? formatRange(optimalRange, unit)
                                    : ''}
                                </Td>
                              </Tr>
                            )
                          }
                        )}
                      </Fragment>
                    )
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </Stack>

          <BloodWorkActionButtons
            onSubmitFunc={wrappedSubmitFunc}
            submitButtonText={submitButtonText}
            submitButtonColorScheme={submitButtonColorScheme}
            isLoading={buttonLoading}
            isDisabled={buttonDisabled}
            cancelFunc={cancelFunc}
            archiveFunc={wrappedArchiveFunc}
          />
        </FormControl>
      )}
    </Stack>
  )
}

export const calculateRatio = (
  panel: UserPanel,
  dividendTitle: string,
  divisorTitle: string,
  values?: CalculatedValues
): string => {
  let dividend = 0
  let divisor = 0

  panel.items?.forEach((item) => {
    if (item.title === dividendTitle) {
      const value = values?.dividend ?? item.result
      dividend = parseFloat(value + '')
    }
    if (item.title === divisorTitle) {
      const value = values?.divisor ?? item.result
      divisor = parseFloat(value + '')
    }
  })

  if (isNaN(dividend) || isNaN(divisor) || divisor === 0) {
    return ''
  }

  return (dividend / divisor).toFixed(2)
}
